// Browser-to-browser audio mesh via PeerJS + WebRTC DataChannel.
//
// We deliberately do NOT use WebRTC MediaConnection here. MediaStream
// transport hides the per-packet timestamps and gives each receiver
// its own opaque jitter buffer, which means peers can't agree on a
// shared timeline — they end up tens to hundreds of milliseconds
// apart. That's fine for a phone call; not fine for "speakers
// playing the same note at the same time."
//
// Instead, one DataChannel per peer. The host pumps timestamped audio
// frames over it; the joiner runs the same NTP-style clock-sync and
// anchor-chain Scheduler we already wrote for the LAN path. Inter-
// device skew lands in the ~10 ms range, same as the Rust pipeline.

import Peer, { type DataConnection } from 'peerjs'

export type PeerEvent =
  | { type: 'open'; id: string }
  | { type: 'connected'; peerId: string }
  | { type: 'disconnected'; peerId: string }
  | { type: 'data'; peerId: string; data: ArrayBuffer }
  | { type: 'host-disconnected' }
  | { type: 'connection-lost' }
  | { type: 'error'; message: string }

/**
 * The shared surface HostStreamer + P2pSession bind to. Both the
 * PeerJS-based `PeerHub` (online mode) and the manual-signalling
 * `OfflinePeerHub` (offline pairing) implement it. Decoupling the
 * audio code from the transport class means we don't have to fork
 * the streamer or session for offline mode.
 */
export interface MeshLink extends EventTarget {
  broadcast(buffer: ArrayBuffer): void
  sendTo(peerId: string, buffer: ArrayBuffer): void
  sendToHost(buffer: ArrayBuffer): void
  peerIds(): string[]
}

export class PeerHub extends EventTarget {
  private peer: Peer | null = null
  private isDestroyed = false
  /** Host: one DataConnection per joined peer. Joiner: a single
   *  entry under the host's id. Either way, conns.values() is "every
   *  channel we'd send through." */
  private conns = new Map<string, DataConnection>()

  async startAsHost(): Promise<string> {
    for (let attempt = 0; attempt < 4; attempt++) {
      const preferred = attempt < 3 ? generateRoomCode() : undefined
      this.peer = preferred
        ? new Peer(preferred, { debug: 1 })
        : new Peer({ debug: 1 })
      try {
        const id = await this.waitForOpen()
        this.peer.on('connection', (conn) => this.adoptConn(conn))
        this.peer.on('error', (e) =>
          this.emit({ type: 'error', message: e.message ?? String(e) }),
        )
        this.emit({ type: 'open', id })
        return id
      } catch (e) {
        try { this.peer.destroy() } catch { /* noop */ }
        const msg = (e as Error).message
        if (!/unavailable|taken|already/i.test(msg) || attempt >= 3) throw e
      }
    }
    throw new Error("Couldn't acquire a room code")
  }

  async startAsJoiner(hostId: string): Promise<void> {
    this.peer = new Peer({ debug: 1 })
    const myId = await this.waitForOpen()
    this.emit({ type: 'open', id: myId })

    this.peer.on('error', (e) =>
      this.emit({ type: 'error', message: e.message ?? String(e) }),
    )

    // serialization: 'raw' — skip BinaryPack wrapping so an ArrayBuffer
    // we send arrives as a raw ArrayBuffer on the other side. The
    // default ('binary' / BinaryPack) silently rewraps payloads in
    // ways that broke deliverData() for us in practice.
    // (PeerJS 1.5 calls this mode 'raw', not 'none'.)
    const conn = this.peer.connect(hostId, { reliable: true, serialization: 'raw' })
    // eslint-disable-next-line no-console
    console.info(`[Multispeaker mesh] dialling host ${hostId}…`)
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error("Couldn't reach host within 10 s")),
        10_000,
      )
      conn.on('open', () => {
        clearTimeout(timeout)
        this.adoptConn(conn)
        resolve()
      })
      conn.on('error', (e) => {
        clearTimeout(timeout)
        reject(new Error(e.message ?? String(e)))
      })
    })
    conn.on('close', () => {
      if (!this.isDestroyed) {
        this.emit({ type: 'connection-lost' })
      } else {
        this.emit({ type: 'host-disconnected' })
      }
    })
  }

  async reconnectAsJoiner(hostId: string): Promise<void> {
    if (this.isDestroyed || !this.peer) return
    const conn = this.peer.connect(hostId, { reliable: true, serialization: 'raw' })
    // eslint-disable-next-line no-console
    console.info(`[Multispeaker mesh] attempting to reconnect to host ${hostId}…`)
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error("Reconnection timed out")),
        5000,
      )
      conn.on('open', () => {
        clearTimeout(timeout)
        this.adoptConn(conn)
        resolve()
      })
      conn.on('error', (e) => {
        clearTimeout(timeout)
        reject(new Error(e.message ?? String(e)))
      })
    })
    conn.on('close', () => {
      if (!this.isDestroyed) {
        this.emit({ type: 'connection-lost' })
      } else {
        this.emit({ type: 'host-disconnected' })
      }
    })
  }

  /** Send a binary frame to a specific peer. */
  sendTo(peerId: string, buffer: ArrayBuffer): void {
    const c = this.conns.get(peerId)
    if (c && c.open) c.send(buffer)
  }

  /** Host: send a binary frame to every connected peer. */
  broadcast(buffer: ArrayBuffer): void {
    for (const c of this.conns.values()) {
      if (c.open) c.send(buffer)
    }
  }

  /** Joiner: send a binary frame back to the host. */
  sendToHost(buffer: ArrayBuffer): void {
    const first = this.conns.values().next().value as DataConnection | undefined
    if (first && first.open) first.send(buffer)
  }

  peerIds(): string[] {
    return Array.from(this.conns.keys())
  }

  destroy() {
    this.isDestroyed = true
    for (const c of this.conns.values()) {
      try { c.close() } catch { /* noop */ }
    }
    this.conns.clear()
    this.peer?.destroy()
    this.peer = null
  }

  private adoptConn(conn: DataConnection) {
    const finish = () => {
      this.conns.set(conn.peer, conn)
      // eslint-disable-next-line no-console
      console.info(
        `[Multispeaker mesh] peer ${conn.peer} connected (serialization=${conn.serialization})`,
      )
      this.emit({ type: 'connected', peerId: conn.peer })
      conn.on('data', (raw) => this.deliverData(conn.peer, raw))
      conn.on('close', () => {
        this.conns.delete(conn.peer)
        this.emit({ type: 'disconnected', peerId: conn.peer })
      })
      conn.on('error', () => {
        this.conns.delete(conn.peer)
        this.emit({ type: 'disconnected', peerId: conn.peer })
      })
    }
    if (conn.open) finish()
    else conn.on('open', finish)
  }

  private inboundCount = 0
  private deliverData(peerId: string, raw: unknown) {
    // Normalise whatever PeerJS / the RTCDataChannel hands us into an
    // ArrayBuffer. With serialization:'none' modern browsers deliver
    // ArrayBuffer directly, but we also accept Uint8Array / Blob /
    // ArrayBufferView shapes other paths might produce.
    let buf: ArrayBuffer | null = null
    let shape = 'unknown'
    if (raw instanceof ArrayBuffer) {
      buf = raw
      shape = 'ArrayBuffer'
    } else if (raw instanceof Uint8Array) {
      const copy = new Uint8Array(raw.byteLength)
      copy.set(raw)
      buf = copy.buffer
      shape = 'Uint8Array'
    } else if (ArrayBuffer.isView(raw)) {
      const view = raw as ArrayBufferView
      const copy = new Uint8Array(view.byteLength)
      copy.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength))
      buf = copy.buffer
      shape = 'ArrayBufferView'
    } else if (typeof Blob !== 'undefined' && raw instanceof Blob) {
      shape = 'Blob'
      // Asynchronous — re-emit after read.
      raw.arrayBuffer().then((ab) => this.emit({ type: 'data', peerId, data: ab }))
        .catch(() => { /* swallow */ })
    }

    if (this.inboundCount < 5) {
      this.inboundCount++
      // eslint-disable-next-line no-console
      console.info(
        `[Multispeaker mesh] inbound #${this.inboundCount} from ${peerId}: shape=${shape}, bytes=${buf ? buf.byteLength : (raw as { size?: number; byteLength?: number })?.size ?? (raw as { byteLength?: number })?.byteLength ?? '?'}`,
      )
    }

    if (buf) this.emit({ type: 'data', peerId, data: buf })
  }

  private async waitForOpen(): Promise<string> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('PeerJS signaling timed out')),
        10_000,
      )
      this.peer!.on('open', (id) => {
        clearTimeout(timeout)
        resolve(id)
      })
      this.peer!.on('error', (e) => {
        clearTimeout(timeout)
        reject(new Error(e.message ?? String(e)))
      })
    })
  }

  private emit(ev: PeerEvent) {
    this.dispatchEvent(new CustomEvent<PeerEvent>('peer', { detail: ev }))
  }
}

export function buildJoinUrl(peerId: string): string {
  const u = new URL(window.location.href)
  u.search = ''
  u.searchParams.set('join', peerId)
  return u.toString()
}

function generateRoomCode(): string {
  const alphabet = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'
  let s = ''
  const rng = crypto.getRandomValues(new Uint8Array(6))
  for (const byte of rng) s += alphabet[byte % alphabet.length]
  return s
}

export function displayCode(id: string): string {
  if (id.length <= 6) return id.length === 6 ? `${id.slice(0, 3)} ${id.slice(3)}` : id
  const head = id.replace(/-/g, '').slice(0, 8).toUpperCase()
  return `${head.slice(0, 4)} ${head.slice(4)}`
}
